"use client";

import type { JSX } from "react";
import { useMemo, useState } from "react";
import { Input, Pagination, StatusMessage, Text, View } from "@/components";
import useTable from "@/hooks/useTable";
import { useLayout } from "@/providers";
import type { CourseEnrollmentRow } from "@/types/elearning";
import { dateForm } from "@/utils/date";

/** Filas por página (paginación client-side). */
const ITEMS_PER_PAGE = 10;

export interface EnrollmentListProps {
  /** Filas ya cargadas por la página dueña (el organismo no fetchea). */
  rows: CourseEnrollmentRow[];
  /** Clave i18n del estado vacío (por defecto "noEnrolledStudents"). */
  emptyTitleKey?: string;
}

/**
 * @name EnrollmentList
 * @description Lista de inscriptos y su progreso (alumno, progreso, estado, fecha) con
 * buscador por usuario y paginación client-side. Es presentacional: recibe las filas ya
 * cargadas. Se comparte entre la gestión del curso y el padrón de Reportes. La API expone
 * el `user_id`; la resolución de nombres queda para cuando el back la incorpore.
 * @param rows Inscripciones a listar.
 * @param emptyTitleKey Clave i18n del estado vacío.
 * @returns Tabla de inscriptos con buscador y paginación.
 */
const EnrollmentList = ({
  rows,
  emptyTitleKey = "noEnrolledStudents"
}: EnrollmentListProps): JSX.Element => {
  const { t } = useLayout();
  const [search, setSearch] = useState("");

  // Filtro por usuario memoizado (solo recalcula al cambiar filas o término).
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term
      ? rows.filter((row) => row.userId.toLowerCase().includes(term))
      : rows;
  }, [rows, search]);

  const table = useTable(filteredRows, ITEMS_PER_PAGE);

  return (
    <View className="flex flex-col gap-4">
      <Input
        id="buscar-inscripto"
        name="buscar-inscripto"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t("searchByUser")}
        className="max-w-xs px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-background text-sm text-foreground"
      />

      {table.totalData === 0 ? (
        <StatusMessage tone="empty" title={t(emptyTitleKey)} />
      ) : (
        <>
          <View className="box rounded-2xl border border-slate-200 dark:border-slate-800 overflow-x-auto">
            <View type="table" className="w-full text-sm">
              <View type="thead">
                <View
                  type="tr"
                  className="text-left text-xs text-muted-foreground border-b border-slate-100 dark:border-slate-800"
                >
                  <View type="th" className="p-4 font-medium">
                    {t("student")}
                  </View>
                  <View type="th" className="p-4 font-medium">
                    {t("progress")}
                  </View>
                  <View type="th" className="p-4 font-medium">
                    {t("status")}
                  </View>
                  <View type="th" className="p-4 font-medium">
                    {t("enrolledAt")}
                  </View>
                </View>
              </View>
              <View type="tbody">
                {table.tableData.map((row) => (
                  <View
                    key={row.enrollmentId}
                    type="tr"
                    className="border-b border-slate-100 dark:border-slate-800 last:border-b-0"
                  >
                    <View type="td" className="p-4">
                      <Text
                        type="span"
                        className="font-mono text-xs text-foreground"
                      >
                        {row.userId}
                      </Text>
                    </View>
                    <View type="td" className="p-4 min-w-[160px]">
                      <View className="flex items-center gap-2">
                        <View className="flex-1 h-2 rounded-full bg-primary/15 overflow-hidden">
                          <View
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${row.progress}%` }}
                          />
                        </View>
                        <Text
                          type="span"
                          className="text-xs font-medium text-muted-foreground shrink-0"
                        >
                          {`${row.progress}%`}
                        </Text>
                      </View>
                    </View>
                    <View type="td" className="p-4">
                      <Text
                        type="span"
                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          row.status === "approved"
                            ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                            : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                        }`}
                      >
                        {row.status === "approved"
                          ? t("approved")
                          : t("inProgress")}
                      </Text>
                    </View>
                    <View type="td" className="p-4">
                      <Text
                        type="span"
                        className="text-xs text-muted-foreground"
                      >
                        {row.enrolledAt
                          ? dateForm(row.enrolledAt, "shortDate")
                          : "—"}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </View>

          {table.totalPages > 1 && (
            <Pagination
              totalPages={table.totalPages}
              currentPage={table.currentPage}
              goToPage={table.paginate}
              nextPage={table.nextPage}
              prevPage={table.prevPage}
              startIndex={table.startIndex}
              endIndex={table.endIndex}
              total={table.totalData}
            />
          )}
        </>
      )}
    </View>
  );
};

export default EnrollmentList;
